import pluralize from 'pluralize';
import { memo } from 'react';

import { RefreshCcwIcon } from '@air/design-icons';
import { FileStatusIconErrored, FileStatusTitle } from '@air/feature-file-status/ui';

import { getTaskPaneFileStatusIconSize } from '~/components/FileStatusTrackingPane/getTaskPaneFileStatusIconSize';
import { PaneItemTW } from '~/components/FileStatusTrackingPane/PaneItemTW';
import { TaskPaneActionButton } from '~/components/FileStatusTrackingPane/TaskPaneActionButton';
import { TaskPaneDismissButton } from '~/components/FileStatusTrackingPane/TaskPaneDismissButton';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { type GetErrorMetadata, type ZippingTask } from '~/store/tasks/types';

export type ZippingTaskFailedPanelItemProps = {
  metadata: GetErrorMetadata<ZippingTask>;
  onClear: () => void;
  onRetry: () => void;
};

export const ZippingTaskFailedPanelItem = memo(
  ({
    metadata: { clipIds = [], boardIds = [], collectionIds = [] },
    onClear,
    onRetry,
  }: ZippingTaskFailedPanelItemProps) => {
    const { ui2026 } = useTemporaryFeatureFlags();
    const itemCount = clipIds.length + boardIds.length + collectionIds.length;
    const title = ui2026
      ? `Couldn’t zip ${pluralize('item', itemCount, true)}`
      : `Failed to zip ${pluralize('item', itemCount, true)}`;

    return (
      <PaneItemTW
        avatar={<FileStatusIconErrored size={getTaskPaneFileStatusIconSize(ui2026)} />}
        title={<FileStatusTitle data-testid="ZIPPING_PANEL_FAILED_TITLE">{title}</FileStatusTitle>}
        buttons={
          <>
            {ui2026 ? (
              <TaskPaneActionButton
                data-testid="ZIPPING_PANEL_FAILED_RETRY_BUTTON"
                icon={<RefreshCcwIcon size="sm" />}
                onClick={onRetry}
              >
                Retry
              </TaskPaneActionButton>
            ) : null}
            <TaskPaneDismissButton label="Clear" onClick={onClear} />
          </>
        }
      />
    );
  },
);

ZippingTaskFailedPanelItem.displayName = 'ZippingTaskFailedPanelItem';
